import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Target } from 'lucide-react';
import {
  OUTCOMES_GUIDANCE,
  movedOutcomeOrder,
  outcomeCandidates,
  outcomeProblem,
  rankedOutcomes,
  retiredOutcomes,
} from '@cerebro-diem/core';
import type { Outcome } from '@cerebro-diem/core';
import { useContainers } from '../../services/containers';
import { errorMessage } from '../../services/errors';
import { addOutcome, reorderOutcomes, retireOutcome, rewordOutcome, useOutcomes } from '../../services/outcomes';
import { queryKeys } from '../../services/queries';
import ScoringStatus from './ScoringStatus';
import { iconButton, inputClass, outlineButton, primaryButton } from '../../components/styles';

/**
 * What matters most (CER-179): the short ranked list of outcomes the person
 * is working toward. They add, reword, reorder and retire them here; the
 * weekly review brings them back to this page to ask whether the list is
 * still right. Candidates are offered from their projects and areas.
 */
export default function OutcomesPage() {
  const queryClient = useQueryClient();
  const outcomesQuery = useOutcomes();
  const containersQuery = useContainers();
  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.outcomes });
  const reorder = useMutation({ mutationFn: (ids: string[]) => reorderOutcomes(ids), onSettled: refresh });
  const retire = useMutation({ mutationFn: (id: string) => retireOutcome(id), onSettled: refresh });
  const [editing, setEditing] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');

  if (outcomesQuery.isPending) return <p className="text-sm text-on-surface-variant">Loading…</p>;
  if (outcomesQuery.isError) return <p className="text-sm text-error">Could not load your outcomes: {errorMessage(outcomesQuery.error)}</p>;

  const all = outcomesQuery.data;
  const ranked = rankedOutcomes(all);
  const retired = retiredOutcomes(all);
  const candidates = containersQuery.data ? outcomeCandidates(containersQuery.data, all) : [];
  const busy = reorder.isPending || retire.isPending;
  const move = (id: string, direction: 'up' | 'down') => {
    const order = movedOutcomeOrder(ranked, id, direction);
    if (order) reorder.mutate(order);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Target className="w-8 h-8 text-primary" />
        <h1 className="text-2xl font-bold">What matters most</h1>
      </div>
      <p className="text-sm text-on-surface-variant">{OUTCOMES_GUIDANCE}</p>
      <ScoringStatus />

      {reorder.isError && <p role="alert" className="text-sm text-error">Order not saved: {errorMessage(reorder.error)}</p>}
      {retire.isError && <p role="alert" className="text-sm text-error">Not retired: {errorMessage(retire.error)}</p>}

      {ranked.length === 0 ? (
        <p className="text-sm">
          Nothing is on the list yet. The Android app can interview you for it: What matters most, then Interview me. Or add the
          first outcome below.
        </p>
      ) : (
        <ol aria-label="Outcomes, most important first" className="space-y-3">
          {ranked.map((outcome, index) => (
            <li key={outcome.id} className="bg-surface rounded-xl p-4 shadow-sm">
              {editing === outcome.id ? (
                <OutcomeForm
                  key={outcome.id}
                  outcome={outcome}
                  others={all.filter((o) => o.id !== outcome.id)}
                  onDone={() => setEditing(null)}
                />
              ) : (
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 w-7 shrink-0 text-lg font-semibold text-primary" aria-hidden="true">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="font-medium break-words">{outcome.title}</p>
                    {outcome.description && <p className="text-sm text-on-surface-variant whitespace-pre-wrap break-words">{outcome.description}</p>}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <button type="button" onClick={() => setEditing(outcome.id)} disabled={busy} className={outlineButton}>
                        Reword
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Retire "${outcome.title}"? It leaves the list and keeps its words.`)) retire.mutate(outcome.id);
                        }}
                        disabled={busy}
                        className={outlineButton}
                      >
                        Retire
                      </button>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col">
                    <button
                      type="button"
                      aria-label={`Move "${outcome.title}" up`}
                      onClick={() => move(outcome.id, 'up')}
                      disabled={busy || index === 0}
                      className={`${iconButton} rounded-lg hover:bg-surface-variant disabled:opacity-30`}
                    >
                      <ArrowUp className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Move "${outcome.title}" down`}
                      onClick={() => move(outcome.id, 'down')}
                      disabled={busy || index === ranked.length - 1}
                      className={`${iconButton} rounded-lg hover:bg-surface-variant disabled:opacity-30`}
                    >
                      <ArrowDown className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      <section aria-labelledby="add-outcome-heading" className="bg-surface rounded-xl p-4 shadow-sm space-y-3">
        <h2 id="add-outcome-heading" className="text-lg font-semibold">
          Add an outcome
        </h2>
        <OutcomeForm key={`new:${draftTitle}`} startTitle={draftTitle} others={all} onDone={() => setDraftTitle('')} />
        {containersQuery.isError && (
          <p className="text-sm text-error">Could not read your projects and areas for suggestions: {errorMessage(containersQuery.error)}</p>
        )}
        {candidates.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm text-on-surface-variant">From your projects and areas:</p>
            <div className="flex flex-wrap gap-2">
              {candidates.map((candidate) => (
                <button
                  key={`${candidate.kind}:${candidate.name}`}
                  type="button"
                  onClick={() => setDraftTitle(candidate.name)}
                  className={outlineButton}
                >
                  {candidate.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {retired.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-on-surface-variant">Retired ({retired.length})</summary>
          <ul className="mt-2 space-y-2">
            {retired.map((outcome) => (
              <li key={outcome.id}>
                <span className="font-medium">{outcome.title}</span>
                <span className="text-on-surface-variant"> · retired {outcome.retired_at.slice(0, 10)}</span>
                {outcome.description && <p className="text-on-surface-variant whitespace-pre-wrap">{outcome.description}</p>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** The title and description of one outcome: a new one (no `outcome`), or one being reworded. */
function OutcomeForm({ outcome, startTitle = '', others, onDone }: { outcome?: Outcome; startTitle?: string; others: Outcome[]; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(outcome?.title ?? startTitle);
  const [description, setDescription] = useState(outcome?.description ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => (outcome ? rewordOutcome(outcome.id, title, description) : addOutcome(title, description)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.outcomes });
      if (!outcome) {
        setTitle('');
        setDescription('');
      }
      onDone();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = outcomeProblem(title, description, others);
    setProblem(found);
    if (found === null) save.mutate();
  };
  const label = outcome ? `Reword "${outcome.title}"` : 'New outcome';
  return (
    <form onSubmit={submit} aria-label={label} className="space-y-2">
      <input
        aria-label="Outcome"
        className={inputClass}
        placeholder="The result you are working toward"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />
      <textarea
        aria-label="What it means and why it matters"
        className={inputClass}
        rows={2}
        placeholder="What it means and why it matters, in a sentence or two"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={save.isPending} className={primaryButton}>
          {save.isPending ? 'Saving…' : outcome ? 'Save' : 'Add'}
        </button>
        {outcome && (
          <button type="button" onClick={onDone} disabled={save.isPending} className={outlineButton}>
            Cancel
          </button>
        )}
        {problem && <p className="text-sm text-error">{problem}</p>}
        {save.isError && <p className="text-sm text-error">Not saved: {errorMessage(save.error)}</p>}
      </div>
    </form>
  );
}
